/**
 * Shared guards for the code sandboxes (`build_spatial`, `build_mesh`).
 *
 * Both tools run model-authored JavaScript through `new Function`. The safety
 * story is identical in both and has been paid for once already in bench
 * failures, so it lives in one place rather than being re-derived per sandbox:
 *
 * - a small source-pattern ban for the escapes that a shadowed parameter
 *   cannot cover (`eval`, dynamic `import`, `fetch`, timers),
 * - host globals bound to `undefined` as the sandbox function's leading
 *   parameters rather than word-banned, because "window" is what every
 *   building script calls a window and a word ban rejected three architecture
 *   builds in a row (bench 2026-09-10),
 * - a loop guard injected into every loop body, because the sandbox is
 *   synchronous and nothing else can stop `while (true)`.
 */

/** Source patterns rejected before execution. */
export const FORBIDDEN_PATTERNS: [RegExp, string][] = [
    [/\beval\s*\(/g, '`eval()` is forbidden'],
    [/\bnew\s+Function\s*\(/g, '`new Function()` is forbidden'],
    [/\bsetTimeout\s*\(/g, '`setTimeout()` is forbidden'],
    [/\bsetInterval\s*\(/g, '`setInterval()` is forbidden'],
    [/\brequestAnimationFrame\s*\(/g, '`requestAnimationFrame()` is forbidden'],
    // `window`, `document`, `process`, `globalThis` are NOT pattern-banned:
    // "window" is what every building script calls a window, and a word ban
    // rejected three architecture builds in a row (bench 2026-09-10). They are
    // shadowed as `undefined` parameters of the sandbox function instead — see
    // SHADOWED_GLOBALS — so a bare reference throws and a local declaration
    // simply wins.
    [/\bimport\s*\(/g, 'Dynamic `import()` is forbidden'],
    [/\brequire\s*\(/g, '`require()` is forbidden'],
    [/\bfetch\s*\(/g, '`fetch()` is forbidden in sandboxed builds'],
];

/**
 * Host globals the model's code must not reach. Bound to `undefined` as the
 * sandbox function's first parameters, which beats a word ban: the names stay
 * usable as ordinary identifiers, and a bare reference fails at runtime.
 */
export const SHADOWED_GLOBALS = ['window', 'document', 'process', 'globalThis', 'self'] as const;

/** Source-level violations, empty when the code is acceptable. */
export function validateSource(code: string): string[] {
    const violations: string[] = [];
    for (const [pattern, message] of FORBIDDEN_PATTERNS) {
        pattern.lastIndex = 0;
        if (pattern.test(code)) {
            violations.push(message);
        }
    }
    return violations;
}

/**
 * Inject an iteration guard into every for/while/do body.
 *
 * `extraGuard` is appended inside the same injected statement — `build_mesh`
 * uses it for a wall-clock deadline, which an iteration count alone cannot
 * express (a thousand CSG subtractions is a legal iteration count and an
 * illegal amount of time).
 */
export function instrumentLoopGuards(
    code: string,
    maxIterations: number,
    extraGuard = '',
): string {
    const guard = `if(++__loopCount>${maxIterations})throw new Error("Loop limit exceeded (${maxIterations} iterations)");${extraGuard}`;

    // Insert guard after opening brace of for/while/do blocks
    // Matches: for (...) { | while (...) { | do {
    let result = code.replace(
        /\b(for\s*\([^)]*\)\s*\{)/g,
        `$1${guard}`,
    );
    result = result.replace(
        /\b(while\s*\([^)]*\)\s*\{)/g,
        `$1${guard}`,
    );
    result = result.replace(
        /\b(do\s*\{)/g,
        `$1${guard}`,
    );

    return result;
}
